import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Play, Clock, MapPin } from "lucide-react";
import { toast } from "sonner";
import { FilterPillsSkeleton, TimetableGridSkeleton } from "@/components/skeletons/PageSkeletons";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export default function Timetable() {
  const [slots, setSlots] = useState([]);
  const [sections, setSections] = useState([]);
  const [sectionId, setSectionId] = useState("all");
  const [activeDay, setActiveDay] = useState(DAYS[(new Date().getDay() + 6) % 7]);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    Promise.all([
      api.get("/timetable").then(r => setSlots(r.data)),
      api.get("/sections").then(r => setSections(r.data)),
    ]).finally(() => setLoading(false));
  }, []);

  const startClass = async (slotId) => {
    setStarting(slotId);
    try {
      const { data } = await api.post("/sessions/start", { slot_id: slotId });
      toast.success("Session started");
      navigate(`/sessions/${data.id}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not start session");
    } finally {
      setStarting(null);
    }
  };

  const filtered = slots.filter(s =>
    s.day === activeDay && (sectionId === "all" || s.section_id === sectionId)
  );
  const todayLabel = DAYS[(new Date().getDay() + 6) % 7];

  return (
    <div className="min-h-screen">
      <div className="px-6 md:px-12 pt-10 pb-6">
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#94A3B8]">Weekly schedule</div>
        <h1 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tighter text-[#0F172A] mt-2" data-testid="timetable-heading">
          Timetable.
        </h1>
        <p className="text-[#475569] mt-3 max-w-2xl">Each section has its own daily schedule. Pick a section, pick a day, then tap Start class on any period to open the roll-call.</p>
      </div>

      {/* Section chooser */}
      <div className="px-6 md:px-12 pb-4">
        {loading ? (
          <FilterPillsSkeleton count={5} />
        ) : (
        <div className="flex items-center gap-2 flex-wrap" data-testid="section-filters">
          <button
            onClick={() => setSectionId("all")}
            data-testid="section-filter-all"
            className={`px-4 py-2 rounded-full text-xs font-semibold transition-colors duration-200 ease-in-out ${
              sectionId === "all" ? "bg-[#C8102E] text-white" : "bg-white border border-[#E2E8F0] text-[#475569] hover:bg-[#F1F5F9]"
            }`}
          >
            All sections
          </button>
          {sections.map(s => (
            <button
              key={s.id}
              onClick={() => setSectionId(s.id)}
              data-testid={`section-filter-${s.id}`}
              className={`px-4 py-2 rounded-full text-xs font-semibold transition-colors duration-200 ease-in-out ${
                sectionId === s.id ? "bg-[#C8102E] text-white" : "bg-white border border-[#E2E8F0] text-[#475569] hover:bg-[#F1F5F9]"
              }`}
            >
              Section {s.name}
            </button>
          ))}
        </div>
        )}
      </div>

      {/* Day tabs */}
      <div className="px-6 md:px-12 border-b border-[#E2E8F0]">
        {loading ? (
          <div className="py-3">
            <FilterPillsSkeleton count={7} />
          </div>
        ) : (
        <div className="flex gap-1 overflow-x-auto" data-testid="day-tabs">
          {DAYS.map(d => {
            const active = d === activeDay;
            const isToday = d === todayLabel;
            return (
              <button
                key={d}
                onClick={() => setActiveDay(d)}
                data-testid={`day-tab-${d}`}
                className={`px-5 py-3 text-sm font-semibold border-b-2 transition-colors duration-200 ease-in-out whitespace-nowrap ${
                  active ? "border-[#C8102E] text-[#C8102E]" : "border-transparent text-[#475569] hover:text-[#0F172A]"
                }`}
              >
                {d}
                {isToday && <span className="ml-2 text-[10px] font-bold uppercase tracking-widest text-[#059669]">Today</span>}
              </button>
            );
          })}
        </div>
        )}
      </div>

      <div className="px-6 md:px-12 py-8">
        {loading ? (
          <TimetableGridSkeleton />
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 text-[#94A3B8]" data-testid="timetable-empty">No classes scheduled for this filter.</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="slots-grid">
            {filtered.map(slot => (
              <div
                key={slot.id}
                className="bg-white border border-[#E2E8F0] rounded-lg p-6 hover:shadow-sm transition-shadow duration-200 ease-in-out"
                data-testid={`slot-${slot.id}`}
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-[0.15em] text-[#C8102E] bg-[#FDECEF] px-2 py-1 rounded">
                    <Clock className="w-3 h-3" />
                    {slot.start_time} – {slot.end_time}
                  </div>
                  <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#94A3B8]">{slot.day}</div>
                </div>
                <div className="font-heading text-xl font-bold tracking-tight text-[#0F172A]">{slot.course_name}</div>
                <div className="text-xs text-[#94A3B8] mt-1 font-bold uppercase tracking-[0.15em]">{slot.course_code}</div>
                <div className="mt-4 pt-4 border-t border-[#E2E8F0] flex items-center justify-between text-sm text-[#475569]">
                  <div className="font-semibold text-[#0F172A]">Section {slot.section_name}</div>
                  {slot.classroom_name && (
                    <div className="flex items-center gap-1 text-xs"><MapPin className="w-3 h-3" />{slot.classroom_name}</div>
                  )}
                </div>
                <button
                  onClick={() => startClass(slot.id)}
                  disabled={starting === slot.id}
                  className="mt-5 w-full inline-flex items-center justify-center gap-2 bg-[#C8102E] hover:bg-[#9F0D24] disabled:opacity-60 text-white py-2.5 rounded-md text-sm font-semibold transition-colors duration-200 ease-in-out"
                  data-testid={`start-class-btn-${slot.id}`}
                >
                  <Play className="w-4 h-4" />
                  {starting === slot.id ? "Starting…" : "Start class"}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
